import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  HostListener,
  Input,
  NgZone,
  OnInit,
  Output,
} from '@angular/core';
import {
  endOfDay,
  endOfMonth,
  format,
  getDaysInMonth,
  isAfter,
  isBefore,
  startOfDay,
  startOfMonth,
} from 'date-fns';
import { DatepickerLocaleService } from '../locale/datepicker-locale.service';

@Component({
  selector: 'lib-datepicker-container',
  templateUrl: './datepicker-container.component.html',
  styleUrls: ['./datepicker-container.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class DatepickerContainerComponent implements OnInit {
  @Input() initialDate: Date;
  @Input() minDate: Date;
  @Input() maxDate: Date;
  @Input() startThisYear: boolean;
  @Input() label: string;

  months: string[];
  year: number;
  month: number;
  yearRangeMin: number;
  yearRangeMax: number;
  selectMode: SelectMode;
  SelectMode = SelectMode;
  focusDate: number;
  focusYear: number;

  @Output() selectedDate = new EventEmitter<Date>();

  skipEndFocus = true;

  get disablePrev() {
    const nextYear = this.month === 0 ? this.year - 1 : this.year;
    const nextMonth = this.month === 0 ? 11 : this.month - 1;

    const next = new Date(nextYear, nextMonth);

    return (
      isAfter(this.minDate, endOfMonth(next)) ||
      isBefore(this.maxDate, startOfMonth(next))
    );
  }

  get disableNext() {
    const nextYear = this.month === 11 ? this.year + 1 : this.year;
    const nextMonth = this.month === 11 ? 0 : this.month + 1;

    const next = new Date(nextYear, nextMonth);

    return (
      isAfter(this.minDate, endOfMonth(next)) ||
      isBefore(this.maxDate, startOfMonth(next))
    );
  }

  constructor(
    private cdr: ChangeDetectorRef,
    private datepickerLocaleService: DatepickerLocaleService,
    private zone: NgZone,
  ) {}

  ngOnInit(): void {
    const today = new Date();
    this.year = this.getStartYear();
    this.month = this.getStartMonth();
    this.yearRangeMin = this.startThisYear ? today.getFullYear() : 1900;
    this.yearRangeMax = 2100;

    this.selectMode = SelectMode.Date;
    this.months = this.datepickerLocaleService.getMonths();
    if (!this.initialDate) {
      this.initialDate = new Date(
        this.minDate
          ? format(this.minDate, 'yyyy-MM-dd')
          : format(new Date(), 'yyyy-MM-dd'),
      );
    }
  }

  private getStartYear() {
    if (
      this.initialDate &&
      !isAfter(this.minDate, endOfDay(this.initialDate)) &&
      !isBefore(this.maxDate, startOfDay(this.initialDate))
    ) {
      return this.initialDate.getFullYear();
    }

    const now = new Date();

    if (this.minDate && isAfter(this.minDate, now)) {
      return this.minDate.getFullYear();
    } else if (this.maxDate && isBefore(this.maxDate, now)) {
      return this.maxDate.getFullYear();
    } else {
      return now.getFullYear();
    }
  }

  private getStartMonth() {
    if (
      this.initialDate &&
      !isAfter(this.minDate, endOfDay(this.initialDate)) &&
      !isBefore(this.maxDate, startOfDay(this.initialDate))
    ) {
      return this.initialDate.getMonth();
    }

    const now = new Date();

    if (this.minDate && isAfter(this.minDate, now)) {
      return this.minDate.getMonth();
    } else if (this.maxDate && isBefore(this.maxDate, now)) {
      return this.maxDate.getMonth();
    } else {
      return now.getMonth();
    }
  }

  next() {
    if (this.disableNext) {
      return;
    }

    if (this.month === 11) {
      this.month = 0;
      this.year++;
    } else {
      this.month++;
    }
  }

  prev() {
    if (this.disablePrev) {
      return;
    }

    if (this.month === 0) {
      this.month = 11;
      this.year--;
    } else {
      this.month--;
    }
  }

  setSelectMode(mode: SelectMode) {
    this.zone.runOutsideAngular(() => {
      setTimeout(() => {
        this.selectMode = mode;

        if (
          this.minDate?.getFullYear() === this.year &&
          this.minDate?.getMonth() > this.month
        ) {
          this.month = this.minDate.getMonth();
        }

        if (
          this.maxDate?.getFullYear() === this.year &&
          this.maxDate?.getMonth() < this.month
        ) {
          this.month = this.maxDate.getMonth();
        }

        this.cdr.detectChanges();
      }, 0);
    });
  }

  selectDate(date: number) {
    const dateOutput = new Date(this.year, this.month, date);
    this.selectedDate.emit(dateOutput);
  }

  selectYear(year: number) {
    this.year = year;
    this.setSelectMode(SelectMode.Date);
  }

  startFocus() {
    if (this.skipEndFocus) {
      this.skipEndFocus = false;
      return;
    }
    const focusElements = document.querySelectorAll(
      '.next-focus:not(:disabled)',
    );
    if (!focusElements || focusElements.length === 0) {
      return;
    }
    ([...focusElements].at(-1) as HTMLElement).focus();
  }

  endFocus() {
    (
      document.querySelector('.next-focus:not(:disabled)') as HTMLElement
    )?.focus();
  }

  @HostListener('keydown.ArrowDown', ['$event'])
  onArrowDown($event) {
    if (this.selectMode === SelectMode.Date) {
      this.dateFoward(7);
    } else if (this.selectMode === SelectMode.Year) {
      this.yearForward(3);
    }
    $event.preventDefault();
  }

  @HostListener('keydown.ArrowUp', ['$event'])
  onArrowUp($event) {
    if (this.selectMode === SelectMode.Date) {
      this.dateBackward(7);
    } else if (this.selectMode === SelectMode.Year) {
      this.yearBackward(3);
    }
    $event.preventDefault();
  }

  @HostListener('keydown.ArrowRight', ['$event'])
  onArrowRight($event) {
    if (this.selectMode === SelectMode.Date) {
      this.dateFoward(1);
    } else if (this.selectMode === SelectMode.Year) {
      this.yearForward(1);
    }
    $event.preventDefault();
  }

  @HostListener('keydown.ArrowLeft', ['$event'])
  onArrowLeft($event) {
    if (this.selectMode === SelectMode.Date) {
      this.dateBackward(1);
    } else if (this.selectMode === SelectMode.Year) {
      this.yearBackward(1);
    }
    $event.preventDefault();
  }

  private dateFoward(steps: number) {
    const baseDate = this.focusDate ?? this.initialDate?.getDate();

    if (!baseDate) {
      this.focusDate = new Date().getDate();
      return;
    }

    this.focusDate =
      ((baseDate + steps - 1) %
        getDaysInMonth(new Date(this.year, this.month, 1))) +
      1;

    if (this.focusDate < baseDate) {
      this.next();
    }
  }

  private dateBackward(steps: number) {
    const baseDate = this.focusDate ?? this.initialDate?.getDate();

    if (!baseDate) {
      this.focusDate = new Date().getDate();
      return;
    }

    const newDate = baseDate - steps;

    if (newDate < 1) {
      this.prev();
      this.focusDate =
        getDaysInMonth(new Date(this.year, this.month, 1)) + newDate;
    } else {
      this.focusDate = newDate;
    }
  }

  private yearForward(steps: number) {
    const baseYear = this.focusYear ?? this.initialDate?.getFullYear();

    if (!baseYear) {
      this.focusYear = new Date().getFullYear();
      return;
    }

    if (baseYear > this.maxDate?.getFullYear()) {
      return;
    }

    this.focusYear = baseYear + steps;
  }

  private yearBackward(steps: number) {
    const baseYear = this.focusYear ?? this.initialDate?.getFullYear();

    if (!baseYear) {
      this.focusYear = new Date().getFullYear();
      return;
    }

    if (baseYear < this.minDate?.getFullYear()) {
      return;
    }

    this.focusYear = baseYear - steps;
  }
}

export enum SelectMode {
  Year,
  Date,
}
